import { Reducer } from 'redux';
import { update } from '../helpers/immutabilityHelper'
import * as Dtos from '../dtos/Tmd.dtos';

import {
    ACTION_TRIALGROUPLABORATORY_LOAD,
    ACTION_TRIALGROUPLABORATORY_LOAD_BY_TRIALGROUPID,
    ACTION_TRIALGROUPLABORATORY_LOAD_BY_TRIALID,
    ACTION_TRIALGROUPLABORATORY_LOAD_BY_ID,
    ACTION_TRIALGROUPLABORATORY_LOAD_BY_LABORATORY_ID,
    ACTION_TRIALGROUPLABORATORY_LOAD_FAILURE,
    ACTION_TRIALGROUPLABORATORY_LOAD_SUCCESS,

    ACTION_TRIALGROUPLABORATORY_SAVE,
    ACTION_TRIALGROUPLABORATORY_SAVE_SUCCESS,
    ACTION_TRIALGROUPLABORATORY_SAVE_FAILURE,

    ACTION_TRIALGROUPLABORATORY_CREATE,
    ACTION_TRIALGROUPLABORATORY_CREATE_SUCCESS,
    ACTION_TRIALGROUPLABORATORY_CREATE_FAILURE,

    ACTION_TRIALGROUPLABORATORY_DELETE,
    ACTION_TRIALGROUPLABORATORY_DELETE_SUCCESS,
    ACTION_TRIALGROUPLABORATORY_DELETE_FAILURE,

    ACTION_TRIALGROUPLABORATORY_VALIDATE,
    ACTION_TRIALGROUPLABORATORY_VALIDATE_SUCCESS,
    ACTION_TRIALGROUPLABORATORY_VALIDATE_FAILURE,

    ACTION_TRIALGROUPLABORATORY_CLEAR,
    ACTION_TRIALGROUPLABORATORY_FILTER,

    TrialGroupLaboratoryAction,
} from '../actions/trialGroupLaboratory';
import { RequestState } from "../enumerations/RequestState";
import { IRequestState, IFormFilter } from "./index";
import { updateFailureIndexToId } from "../helpers/formErrorHelper";

export interface ITrialGroupLaboratoryState {
    formData: Dtos.TrialGroupLaboratory | Dtos.TrialGroupLaboratory[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    validationFailures: Dtos.ResponseError[];
    lookups: Dtos.Lookup[],
    loadState: IRequestState;
    saveState: IRequestState;
    updateState: IRequestState;
    createState: IRequestState;
    deleteState: IRequestState;
    validateState: IRequestState;
    formFilter: IFormFilter;
    formFilteredData: Dtos.TrialGroupLaboratory[];
    formLength: number;
}

const initialState: ITrialGroupLaboratoryState = {
    formData: undefined,
    formState: undefined,
    formProperties: undefined,
    validationFailures: undefined,
    lookups: undefined,
    loadState: {
        status: RequestState.None
    } as IRequestState,
    saveState: {
        status: RequestState.None
    } as IRequestState,
    updateState: {
        status: RequestState.None
    } as IRequestState,
    createState: {
        status: RequestState.None
    } as IRequestState,
    deleteState: {
        status: RequestState.None
    } as IRequestState,
    validateState: {
        status: RequestState.None
    } as IRequestState,
    formFilter: {
        search: "",
        showInActive: false
    },
    formFilteredData: [],
    formLength: 0
};


function loading(state: ITrialGroupLaboratoryState, _isLoading: boolean): ITrialGroupLaboratoryState {
    const newState: ITrialGroupLaboratoryState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function loadSuccess(state: ITrialGroupLaboratoryState,
    formData: Dtos.TrialGroupLaboratory | Dtos.TrialGroupLaboratory[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[],
    responseStatus: Dtos.ResponseStatus
): ITrialGroupLaboratoryState {
    const newState: ITrialGroupLaboratoryState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        loadState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return filter(newState, state.formFilter);
}

function loadFailure(state: ITrialGroupLaboratoryState, responseStatus: Dtos.ResponseStatus): ITrialGroupLaboratoryState {
    const newState: ITrialGroupLaboratoryState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors
            } as IRequestState
        }
    });

    return newState;
}

function filter(state: ITrialGroupLaboratoryState, formFilter: IFormFilter): ITrialGroupLaboratoryState {

    const items: Dtos.TrialGroupLaboratory[] | undefined =
        state.formData instanceof Array ?
            state.formData :
            undefined;

    const formLength = items ? items.length : 0;

    const formFilteredData = items ? items.filter(p => (p.active == true || formFilter.showInActive) &&
        (formFilter.search == "" || p.laboratory?.name?.toLowerCase().indexOf(formFilter.search.toLowerCase()) > -1)) : undefined;

    const newState: ITrialGroupLaboratoryState = update(state, {
        formFilter: {
            $set: formFilter
        },
        formFilteredData: {
            $set: formFilteredData
        },
        formLength: {
            $set: formLength
        }
    });

    return newState;
}

function clear(state: ITrialGroupLaboratoryState, propertiesToClear?: string[]): ITrialGroupLaboratoryState {

    if (!propertiesToClear) {
        const newState: ITrialGroupLaboratoryState = update(initialState, {});
        return newState;
    }

    let stateModifier: any = {};

    propertiesToClear.forEach(propertyToClear => {
        stateModifier[propertyToClear] = { $set: initialState[propertyToClear] }
    });

    const newState = update(state, stateModifier);

    return newState;
}

function saving(state: ITrialGroupLaboratoryState, _isSaving: boolean): ITrialGroupLaboratoryState {
    const newState: ITrialGroupLaboratoryState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function saveSuccess(
    state: ITrialGroupLaboratoryState,
    formData: Dtos.TrialGroupLaboratory | Dtos.TrialGroupLaboratory[],
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): ITrialGroupLaboratoryState {
    const newState: ITrialGroupLaboratoryState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        saveState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function saveFailure(state: ITrialGroupLaboratoryState, responseStatus: Dtos.ResponseStatus): ITrialGroupLaboratoryState {
    const newState: ITrialGroupLaboratoryState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}

function creating(state: ITrialGroupLaboratoryState, _isCreating: boolean): ITrialGroupLaboratoryState {
    const newState: ITrialGroupLaboratoryState = update(state, {
        createState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function createSuccess(
    state: ITrialGroupLaboratoryState,
    formData: Dtos.TrialGroupLaboratory,
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[]
): ITrialGroupLaboratoryState {
    const newState: ITrialGroupLaboratoryState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        createState: {
            $set: {
                status: RequestState.Success
            } as IRequestState
        }
    });

    return newState;
}

function createFailure(state: ITrialGroupLaboratoryState, responseStatus: Dtos.ResponseStatus): ITrialGroupLaboratoryState {
    const newState: ITrialGroupLaboratoryState = update(state, {
        createState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors
            } as IRequestState
        }
    });

    return newState;
}

function deleting(state: ITrialGroupLaboratoryState, _isDeleting: boolean): ITrialGroupLaboratoryState {
    const newState: ITrialGroupLaboratoryState = update(state, {
        createState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function deleteSuccess(
    state: ITrialGroupLaboratoryState
): ITrialGroupLaboratoryState {
    const newState: ITrialGroupLaboratoryState = update(state, {
        deleteState: {
            $set: {
                status: RequestState.Success
            } as IRequestState
        }
    });

    return newState;
}

function deleteFailure(state: ITrialGroupLaboratoryState, responseStatus: Dtos.ResponseStatus): ITrialGroupLaboratoryState {
    const newState: ITrialGroupLaboratoryState = update(state, {
        deleteState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors
            } as IRequestState
        }
    });

    return newState;
}

function validating(state: ITrialGroupLaboratoryState, _isValidating: boolean): ITrialGroupLaboratoryState {
    const newState: ITrialGroupLaboratoryState = update(state, {
        validateState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function validateSuccess(
    state: ITrialGroupLaboratoryState,
    formData: Dtos.TrialGroupLaboratory,
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): ITrialGroupLaboratoryState {
    const newState: ITrialGroupLaboratoryState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        validateState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function validateFailure(state: ITrialGroupLaboratoryState, responseStatus: Dtos.ResponseStatus): ITrialGroupLaboratoryState {
    const newState: ITrialGroupLaboratoryState = update(state, {
        validateState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}

const TrialGroupLaboratoryReducer: Reducer<ITrialGroupLaboratoryState> = (state: ITrialGroupLaboratoryState = initialState, action: TrialGroupLaboratoryAction) => {
    switch (action.type) {
        case ACTION_TRIALGROUPLABORATORY_LOAD:
        case ACTION_TRIALGROUPLABORATORY_LOAD_BY_TRIALGROUPID:
        case ACTION_TRIALGROUPLABORATORY_LOAD_BY_TRIALID:
        case ACTION_TRIALGROUPLABORATORY_LOAD_BY_ID:
        case ACTION_TRIALGROUPLABORATORY_LOAD_BY_LABORATORY_ID:
            return loading(state, true);
        case ACTION_TRIALGROUPLABORATORY_LOAD_SUCCESS:
            return loadSuccess(state, action.formData, action.formState, action.formProperties, action.lookups, action.responseStatus);
        case ACTION_TRIALGROUPLABORATORY_LOAD_FAILURE:
            return loadFailure(state, action.responseStatus);
        case ACTION_TRIALGROUPLABORATORY_CLEAR:
            return clear(state, action.propertiesToClear);
        case ACTION_TRIALGROUPLABORATORY_SAVE:
            return saving(state, true);
        case ACTION_TRIALGROUPLABORATORY_SAVE_SUCCESS:
            return saveSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_TRIALGROUPLABORATORY_SAVE_FAILURE:
            return saveFailure(state, action.responseStatus);
        case ACTION_TRIALGROUPLABORATORY_CREATE:
            return creating(state, true);
        case ACTION_TRIALGROUPLABORATORY_CREATE_SUCCESS:
            return createSuccess(state, action.formData, action.formState, action.formProperties, action.lookups);
        case ACTION_TRIALGROUPLABORATORY_CREATE_FAILURE:
            return createFailure(state, action.responseStatus);
        case ACTION_TRIALGROUPLABORATORY_DELETE:
            return deleting(state, true);
        case ACTION_TRIALGROUPLABORATORY_DELETE_SUCCESS:
            return deleteSuccess(state);
        case ACTION_TRIALGROUPLABORATORY_DELETE_FAILURE:
            return deleteFailure(state, action.responseStatus);
        case ACTION_TRIALGROUPLABORATORY_VALIDATE:
            return validating(state, true);
        case ACTION_TRIALGROUPLABORATORY_VALIDATE_SUCCESS:
            return validateSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_TRIALGROUPLABORATORY_VALIDATE_FAILURE:
            return validateFailure(state, action.responseStatus);
        case ACTION_TRIALGROUPLABORATORY_FILTER:
            return filter(state, action.formFilter);
    }

    return state;
}

export default TrialGroupLaboratoryReducer;